import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchBlogs, fetchFeaturedBlogs } from '../api/blogs';

/**
 * Holds the newest posts, fetched once, so the home page does not refetch the
 * same first page every time the reader navigates back to it.
 *
 * The hero post is fetched SEPARATELY from GET /api/blogs/featured — a single
 * indexed lookup — rather than read out of `latest` by filtering on `featured`.
 * The old approach could only ever see a pin that happened to fall inside the
 * first page, and paid for a whole list read to find it.
 */
const BlogsContext = createContext({
  latest: [],
  featured: null,
  featuredList: [],
  featuredLoading: true,
  hasMore: false,
  loading: true,
  error: null,
  reload: () => {},
});

/*
 * The newest posts, exactly as the table has them — nine, three full rows of
 * three in the home page grid.
 *
 * Pinned posts are NOT excluded from this list. "Latest" means the latest, so a
 * post that is both new and featured appears in the featured block on top and
 * again here, and the grid is always nine regardless of what is pinned.
 *
 * Exported because the home page renders one skeleton per slot; the two must not
 * drift apart.
 */
export const LATEST_LIMIT = 9;

export function BlogsProvider({ children }) {
  const [latest, setLatest] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The whole pinned list, not one post: the home page uses the first as its lead
  // story and any others to open its headline rail.
  const [featuredList, setFeaturedList] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  // Whether the API has posts beyond this first page.
  const [hasMore, setHasMore] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFeaturedLoading(true);
    setError(null);

    /*
     * Two independent requests, in parallel and with independent outcomes: the
     * hero must not hold up the list, and a missing hero is not a page error.
     * fetchFeaturedBlogs never throws, so there is nothing to catch — "nothing is
     * pinned" and "the request failed" both arrive as an empty list, and both
     * simply mean the newest post leads instead.
     */
    fetchFeaturedBlogs()
      .then(setFeaturedList)
      .finally(() => setFeaturedLoading(false));

    try {
      // First page only — the home page shows a fixed number of recent posts, so
      // there is no cursor to follow. `hasMore` is kept even though the cursor is
      // not: it is how the page knows whether "see more" has anything behind it.
      const { blogs, hasMore: more } = await fetchBlogs({ limit: LATEST_LIMIT });
      setLatest(blogs);
      setHasMore(more);
    } catch (err) {
      setError(err.message || 'Could not load articles.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /*
   * Raw data only. Deciding which post leads, which fill the rail and which drop
   * into the grid is the home page's job — it is the only thing that knows those
   * slots exist, and a store that pre-slices for one page's layout has to be
   * re-cut every time that layout changes.
   */
  const value = useMemo(
    () => ({
      latest,
      featuredList,
      featured: featuredList[0] || null,
      featuredLoading,
      hasMore,
      loading,
      error,
      reload: load,
    }),
    [latest, featuredList, featuredLoading, hasMore, loading, error, load]
  );

  return <BlogsContext.Provider value={value}>{children}</BlogsContext.Provider>;
}

export const useBlogs = () => useContext(BlogsContext);
